import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Mail, Shield, Save, CheckCircle, AlertTriangle, Settings as SettingsIcon,
  Sparkles, Send, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { AdminSideNav } from "./AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import EmailDiagnosticsCard from "@/components/admin/EmailDiagnosticsCard";
import api from "@/lib/api";

export default function AdminSystemSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Email Configuration (Microsoft Graph)
  const [emailConfig, setEmailConfig] = useState({
    tenant_id: "",
    client_id: "",
    client_secret: "",
    sender_email: "",
    from_name: "Securado Recruitment",
    enabled: false,
    client_secret_set: false,
  });
  
  // Proctoring Policy
  const [proctoringPolicy, setProctoringPolicy] = useState({
    enabled: true,
    auto_terminate_enabled: false,
    violation_thresholds: {
      tab_switch: 3,
      no_face: 5,
      voice_detected: 3,
      fullscreen_exit: 2,
      multiple_faces: 1
    },
    record_video: true,
    record_audio: true,
    require_fullscreen: true
  });

  // LLM Provider (Phase 4)
  const [llmConfig, setLlmConfig] = useState({
    provider: "anthropic",
    api_key: "",
    model: "",
    base_url: "",
    api_key_set: false,
    source: "env",
    supported_providers: ["anthropic", "openai", "google", "custom"],
  });
  const [llmTesting, setLlmTesting] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  
  useEffect(() => {
    loadSettings();
  }, []);
  
  const loadSettings = async () => {
    try {
      const [emailRes, policyRes, llmRes] = await Promise.all([
        api.get("/admin/settings/email"),
        api.get("/admin/settings/proctoring-policy"),
        api.get("/admin/settings/llm"),
      ]);
      setEmailConfig(emailRes.data);
      setProctoringPolicy(policyRes.data);
      setLlmConfig((prev) => ({ ...prev, ...llmRes.data, api_key: "" }));
      setLoading(false);
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error("Failed to load settings");
      setLoading(false);
    }
  };
  
  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/email", emailConfig);
      toast.success("Email configuration saved");
    } catch (error) {
      toast.error("Failed to save email configuration");
    }
    setSaving(false);
  };

  const testEmail = async () => {
    setEmailTesting(true);
    try {
      const resp = await api.post("/admin/settings/email/test", {});
      toast.success(resp.data.message || "Test email sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test email failed");
    }
    setEmailTesting(false);
  };

  const saveLlmConfig = async () => {
    setSaving(true);
    try {
      const payload = { ...llmConfig };
      // Don't send empty api_key (UI hides existing key behind ****)
      if (!payload.api_key) delete payload.api_key;
      await api.post("/admin/settings/llm", payload);
      toast.success("LLM configuration saved");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save LLM config");
    }
    setSaving(false);
  };

  const testLlm = async () => {
    setLlmTesting(true);
    try {
      const resp = await api.post("/admin/settings/llm/test");
      toast.success(`${resp.data.message} — reply: ${resp.data.sample_reply}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "LLM test failed");
    }
    setLlmTesting(false);
  };
  
  const saveProctoringPolicy = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/proctoring-policy", proctoringPolicy);
      toast.success("Proctoring policy saved");
    } catch (error) {
      toast.error("Failed to save proctoring policy");
    }
    setSaving(false);
  };
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <AdminSideNav />
        <div className="flex-1 flex items-center justify-center">
          <div>Loading settings...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSideNav />
      
      <div className="flex-1">
        <BrandedHeader />
        
        <div className="p-8 max-w-5xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <SettingsIcon className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">System Settings</h1>
            </div>
            <p className="text-gray-600">Configure email & proctoring policies</p>
          </div>
          
          <Tabs defaultValue="llm" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 max-w-2xl">
              <TabsTrigger value="llm" data-testid="settings-tab-llm">
                <Sparkles className="w-4 h-4 mr-2" />
                LLM Provider
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="settings-tab-email">
                <Mail className="w-4 h-4 mr-2" />
                Email (O365)
              </TabsTrigger>
              <TabsTrigger value="diagnostics" data-testid="settings-tab-diagnostics">
                <Send className="w-4 h-4 mr-2" />
                Diagnostics
              </TabsTrigger>
              <TabsTrigger value="proctoring" data-testid="settings-tab-proctoring">
                <Shield className="w-4 h-4 mr-2" />
                Proctoring
              </TabsTrigger>
            </TabsList>

            {/* LLM Provider Tab (Phase 4) */}
            <TabsContent value="llm">
              <Card data-testid="llm-settings-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>AI / LLM Provider</CardTitle>
                      <CardDescription>
                        Configure the LLM used by the 5-agent test generator and AI interview.
                        Keys saved here are used instead of the server's environment variable.
                      </CardDescription>
                    </div>
                    <Badge variant={llmConfig.source === "db" ? "default" : "outline"}>
                      {llmConfig.source === "db" ? "Using DB config" : "Using env fallback"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Provider</Label>
                      <Select
                        value={llmConfig.provider}
                        onValueChange={(v) => setLlmConfig({ ...llmConfig, provider: v })}
                      >
                        <SelectTrigger data-testid="llm-provider-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {llmConfig.supported_providers.map((p) => (
                            <SelectItem key={p} value={p} data-testid={`llm-provider-${p}`}>
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Model</Label>
                      <Input
                        data-testid="llm-model-input"
                        placeholder="e.g., claude-sonnet-4-6"
                        value={llmConfig.model || ""}
                        onChange={(e) => setLlmConfig({ ...llmConfig, model: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>API Key</Label>
                    <Input
                      data-testid="llm-api-key-input"
                      type="password"
                      placeholder={llmConfig.api_key_set ? "•••••• (leave blank to keep existing)" : "Paste provider API key"}
                      value={llmConfig.api_key}
                      onChange={(e) => setLlmConfig({ ...llmConfig, api_key: e.target.value })}
                    />
                  </div>

                  {llmConfig.provider === "custom" && (
                    <div>
                      <Label>Base URL</Label>
                      <Input
                        data-testid="llm-base-url-input"
                        placeholder="https://your-gateway.example.com/v1"
                        value={llmConfig.base_url || ""}
                        onChange={(e) => setLlmConfig({ ...llmConfig, base_url: e.target.value })}
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      data-testid="llm-save-btn"
                      onClick={saveLlmConfig}
                      disabled={saving}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Configuration
                    </Button>
                    <Button
                      data-testid="llm-test-btn"
                      variant="outline"
                      onClick={testLlm}
                      disabled={llmTesting}
                    >
                      {llmTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Test Connection
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email">
              <Card>
                <CardHeader>
                  <CardTitle>O365 Email — Microsoft Graph API</CardTitle>
                  <CardDescription>
                    App-only auth (client credentials) via an Azure AD app registration with <code>Mail.Send</code> permission.
                    SMTP is no longer used.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <Label>Enable Email Service</Label>
                    <Switch
                      data-testid="email-enabled-switch"
                      checked={emailConfig.enabled}
                      onCheckedChange={(checked) =>
                        setEmailConfig({ ...emailConfig, enabled: checked })
                      }
                    />
                  </div>

                  <div>
                    <Label>Tenant ID (Directory ID)</Label>
                    <Input
                      data-testid="email-tenant-id-input"
                      value={emailConfig.tenant_id || ""}
                      onChange={(e) => setEmailConfig({ ...emailConfig, tenant_id: e.target.value })}
                      placeholder="11111111-2222-3333-4444-555555555555"
                    />
                  </div>

                  <div>
                    <Label>Client ID (Application ID)</Label>
                    <Input
                      data-testid="email-client-id-input"
                      value={emailConfig.client_id || ""}
                      onChange={(e) => setEmailConfig({ ...emailConfig, client_id: e.target.value })}
                      placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
                    />
                  </div>

                  <div>
                    <Label>
                      Client Secret
                      {emailConfig.client_secret_set && (
                        <span className="ml-2 text-xs text-green-700">(saved — leave blank to keep)</span>
                      )}
                    </Label>
                    <Input
                      type="password"
                      data-testid="email-client-secret-input"
                      value={emailConfig.client_secret || ""}
                      onChange={(e) => setEmailConfig({ ...emailConfig, client_secret: e.target.value })}
                      placeholder={emailConfig.client_secret_set ? "••••••••" : "Paste client secret VALUE (not ID)"}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sender Email (mailbox to send as)</Label>
                      <Input
                        type="email"
                        data-testid="email-sender-input"
                        value={emailConfig.sender_email || ""}
                        onChange={(e) => setEmailConfig({ ...emailConfig, sender_email: e.target.value })}
                        placeholder="noreply@securado.net"
                      />
                    </div>
                    <div>
                      <Label>From Name</Label>
                      <Input
                        data-testid="email-from-name-input"
                        value={emailConfig.from_name || ""}
                        onChange={(e) => setEmailConfig({ ...emailConfig, from_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <Alert>
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription className="text-xs">
                      The Azure AD app needs <strong>Mail.Send</strong> Application permission (not Delegated) + admin consent.
                      The sender mailbox must exist in the tenant.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-2">
                    <Button data-testid="email-save-btn" onClick={saveEmailConfig} disabled={saving} className="flex-1">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      data-testid="email-test-btn"
                      variant="outline"
                      onClick={testEmail}
                      disabled={emailTesting || !emailConfig.enabled}
                    >
                      {emailTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Test Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Diagnostics Tab (Phase 5 verification) */}
            <TabsContent value="diagnostics">
              <EmailDiagnosticsCard enabled={emailConfig.enabled} />
            </TabsContent>

            {/* Proctoring Tab - Continues below... */}
            <TabsContent value="proctoring">
              <Card>
                <CardHeader>
                  <CardTitle>Proctoring Policy</CardTitle>
                  <CardDescription>Violation thresholds & auto-termination</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg border-red-200">
                    <Label className="font-medium">Auto-Terminate Test</Label>
                    <Switch
                      checked={proctoringPolicy.auto_terminate_enabled}
                      onCheckedChange={(checked) => 
                        setProctoringPolicy({...proctoringPolicy, auto_terminate_enabled: checked})
                      }
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {Object.keys(proctoringPolicy.violation_thresholds).map((key) => (
                      <div key={key} className="p-3 border rounded">
                        <Label className="text-sm capitalize">{key.replace(/_/g, ' ')}</Label>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={proctoringPolicy.violation_thresholds[key]}
                          onChange={(e) => setProctoringPolicy({
                            ...proctoringPolicy,
                            violation_thresholds: {
                              ...proctoringPolicy.violation_thresholds,
                              [key]: parseInt(e.target.value)
                            }
                          })}
                        />
                      </div>
                    ))}
                  </div>
                  
                  <Button onClick={saveProctoringPolicy} disabled={saving} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    Save Policy
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
